/* ===========================================================
   Shell + primitivas das páginas de CONTEÚDO (blog, conceitos, guia,
   calculadoras). Tudo é apresentado como "post": PostHeader (eyebrow +
   h1 + dek + byline + capa) e PostRelated (cards). Chrome escuro
   reaproveitado da landing (lp-root, lp-hero-bg, lp-topnav, legal-doc).

   Links internos usam <Link> → <a href> real (crawlable). O <h1> leva
   id="conteudo-h1": seletor que o prerender espera para serializar.
   =========================================================== */
import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Logo, LucraaMark, Icon } from "../../components/ui.jsx";
import { SiteFooter } from "../../components/site-footer";
import { submitNewsletter } from "../../api/waitlist";
import { newsletterInscrito } from "../../analytics/events";
import { dataBr } from "../../domain/format";
import type { SecaoConteudo } from "../../content/glossario";

/** Migalha de pão visual (o JSON-LD BreadcrumbList vai à parte, na página). */
export function Breadcrumb({ itens }: { itens: ReadonlyArray<{ nome: string; path: string }> }) {
  return (
    <nav aria-label="Trilha" className="conteudo-breadcrumb" style={{ marginBottom: 18 }}>
      <ol
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          listStyle: "none",
          padding: 0,
          margin: 0,
          fontSize: 13,
          color: "var(--ink-soft)",
        }}
      >
        {itens.map((it, i) => (
          <li key={it.path} style={{ display: "flex", gap: 8 }}>
            {i < itens.length - 1 ? (
              <Link to={it.path} style={{ color: "var(--mint)", textDecoration: "none" }}>
                {it.nome}
              </Link>
            ) : (
              <span aria-current="page">{it.nome}</span>
            )}
            {i < itens.length - 1 && <span aria-hidden="true">›</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** Render de seções (h2 + parágrafos). HTML simples e estático no corpo. */
export function Secoes({ secoes }: { secoes: ReadonlyArray<SecaoConteudo> }) {
  return (
    <>
      {secoes.map((s) => (
        <section key={s.h}>
          <h2>{s.h}</h2>
          {s.p.map((par, i) => (
            <p key={i} dangerouslySetInnerHTML={{ __html: par }} />
          ))}
        </section>
      ))}
    </>
  );
}

/** Capa por categoria: gradiente da paleta da marca + ícone marca-d'água.
 *  Sem imagem raster (o site é todo SVG/CSS) — a capa é gerada. */
export function coverInfo(categoria: string): { grad: string; icon: string } {
  switch (categoria) {
    case "ROAS":
      return { grad: "linear-gradient(135deg, #0a6e47 0%, #5fe3a1 100%)", icon: "chart" };
    case "Lucro":
      return { grad: "linear-gradient(135deg, #7d3df0 0%, #f0608f 100%)", icon: "coins" };
    case "Conceitos":
      return { grad: "linear-gradient(135deg, #1aa4bd 0%, #5b6ef0 100%)", icon: "target" };
    case "Guia":
      return { grad: "linear-gradient(135deg, #0a6e47 0%, #1aa4bd 100%)", icon: "sheet" };
    default:
      return { grad: "linear-gradient(135deg, #0a6e47 0%, #22b8cf 100%)", icon: "spark" };
  }
}

/** Cabeçalho editorial do post: categoria + título + dek + byline + capa. */
export function PostHeader({
  categoria,
  titulo,
  dek,
  publicadoEm,
  tempoMin,
}: {
  categoria: string;
  titulo: string;
  dek: string;
  publicadoEm: string;
  tempoMin: number;
}) {
  const { grad, icon } = coverInfo(categoria);
  return (
    <>
      <span className="c-eyebrow">{categoria}</span>
      <h1 id="conteudo-h1">{titulo}</h1>
      <p className="article-dek">{dek}</p>
      <div className="article-byline">
        <span className="ab-av" aria-hidden="true">
          <LucraaMark size={18} tone="#5fe3a1" />
        </span>
        <div>
          <div className="ab-name">Time Lucraa+</div>
          <div className="ab-meta">
            <time dateTime={publicadoEm}>{dataBr(publicadoEm, { longa: true })}</time>
            <span className="dot" aria-hidden="true" />
            <span>{tempoMin} min de leitura</span>
          </div>
        </div>
      </div>
      <div className="article-cover" style={{ background: grad }}>
        <span className="ac-ico" aria-hidden="true">
          <Icon name={icon} size={150} color="rgba(255,255,255,0.20)" />
        </span>
        <span className="blog-cat">{categoria}</span>
      </div>
    </>
  );
}

/** "Continue aprendendo" — links internos em cards (fortalece o cluster). */
export function PostRelated({
  titulo = "Continue aprendendo",
  itens,
}: {
  titulo?: string;
  itens: ReadonlyArray<{ nome: string; path: string }>;
}) {
  if (itens.length === 0) return null;
  return (
    <section className="article-related" aria-label={titulo}>
      <h2>{titulo}</h2>
      <div className="related-grid">
        {itens.map((it) => (
          <Link key={it.path} to={it.path} className="related-card">
            <span>{it.nome}</span>
            <span className="rc-arrow" aria-hidden="true">
              →
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

/** Faixa de conversão — funil conteúdo → demo/produto. */
export function CtaDemo({
  titulo = "Veja o lucro real da sua loja, sem planilha",
  sub = "O Lucraa+ cruza as vendas da Nuvemshop com a Meta e o Google, desconta os custos e mostra o que cada campanha deixa de lucro — com uma recomendação por dia.",
}: {
  titulo?: string;
  sub?: string;
}) {
  return (
    <aside
      className="card"
      style={{ marginTop: 44, padding: 24, display: "grid", gap: 12, textAlign: "center" }}
    >
      {/* cor explícita: dentro de .legal-doc, `strong` é branco — invisível no card claro */}
      <strong style={{ fontSize: 20, color: "var(--ink)" }}>{titulo}</strong>
      <p style={{ margin: 0, color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.55 }}>{sub}</p>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        <Link className="btn btn-primary" to="/demo">
          Ver demonstração ao vivo
        </Link>
        <Link className="btn btn-ghost" to="/">
          Conhecer o Lucraa+
        </Link>
      </div>
    </aside>
  );
}

/** Inscrição na newsletter — captação topo-de-funil no conteúdo. Só e-mail.
 *  Envio OTIMISTA (best-effort): avisa o time por e-mail via /api/newsletter e
 *  registra no Mixpanel; qualquer falha é engolida e a confirmação aparece igual. */
export function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState(""); // anti-bot: humano não preenche
  const [estado, setEstado] = useState<"idle" | "enviando" | "ok">("idle");
  const [erro, setErro] = useState(false);

  async function inscrever(e: FormEvent) {
    e.preventDefault();
    if (estado === "enviando") return;
    if (!/.+@.+\..+/.test(email.trim())) {
      setErro(true);
      return;
    }
    setErro(false);
    setEstado("enviando");
    const origem = window.location.pathname;
    const r = await submitNewsletter(email.trim(), honeypot, origem);
    newsletterInscrito({ origem, ok: r.ok });
    setEstado("ok");
  }

  if (estado === "ok") {
    return (
      <aside
        className="card"
        style={{ marginTop: 44, padding: 24, display: "grid", gap: 8, textAlign: "center" }}
      >
        <span aria-hidden="true" style={{ display: "grid", placeItems: "center" }}>
          <Icon name="checkCircle" size={28} color="var(--lucro)" />
        </span>
        <strong style={{ fontSize: 18, color: "var(--ink)" }}>Inscrição confirmada 🎉</strong>
        <p style={{ margin: 0, color: "var(--ink-soft)", fontSize: 14.5, lineHeight: 1.55 }}>
          Você vai receber os próximos conteúdos sobre lucro, ROAS e tráfego pago no seu e-mail.
        </p>
      </aside>
    );
  }

  return (
    <aside
      className="card"
      style={{ marginTop: 44, padding: 24, display: "grid", gap: 12, textAlign: "center" }}
    >
      <strong style={{ fontSize: 20, color: "var(--ink)" }}>
        Receba lucro, ROAS e tráfego pago no seu e-mail
      </strong>
      <p style={{ margin: 0, color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.55 }}>
        Conteúdo direto para lojistas Nuvemshop que investem em mídia. Sem spam — só o que ajuda a
        vender com lucro.
      </p>
      <form
        onSubmit={inscrever}
        noValidate
        style={{
          display: "flex",
          gap: 10,
          justifyContent: "center",
          flexWrap: "wrap",
          marginTop: 4,
        }}
      >
        <div className="field" style={{ flex: "1 1 240px", maxWidth: 320, textAlign: "left" }}>
          <label htmlFor="nl-email" className="sr-only">
            Seu melhor e-mail
          </label>
          <input
            id="nl-email"
            className={"input" + (erro ? " input-erro" : "")}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (erro) setErro(false);
            }}
            placeholder="voce@sualoja.com.br"
            aria-invalid={erro}
          />
        </div>
        {/* Honeypot fora da tela (não display:none — bots costumam pular ocultos). */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
        />
        <button className="btn btn-primary" type="submit" disabled={estado === "enviando"}>
          {estado === "enviando" ? (
            <>
              <span className="spinner" style={{ marginRight: 8 }} /> Inscrevendo…
            </>
          ) : (
            "Quero receber"
          )}
        </button>
      </form>
      {erro && (
        <p style={{ margin: 0, color: "var(--prejuizo)", fontSize: 13 }}>
          Digite um e-mail válido para se inscrever.
        </p>
      )}
    </aside>
  );
}

/** Header das páginas de conteúdo — enxuto: Logo + "Conheça o Lucraa+" + CTA. */
export function ConteudoNav() {
  return (
    <header className="lp-topnav">
      <div
        className="lp-wrap"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 70,
          gap: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Link to="/" aria-label="Página inicial do Lucraa+" style={{ display: "inline-flex" }}>
            <Logo size={32} color="#fff" />
          </Link>
          <span
            aria-hidden="true"
            style={{ width: 1, height: 22, background: "rgba(255,255,255,0.18)" }}
          />
          <Link
            to="/blog"
            data-cta="conteudo_nav_blog"
            style={{
              color: "#fff",
              fontFamily: "var(--font-display)",
              fontWeight: 700,
              fontSize: 17,
              letterSpacing: "-0.01em",
              textDecoration: "none",
            }}
          >
            Blog
          </Link>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <Link className="lp-link" to="/" data-cta="conteudo_nav_conheca">
            Conheça o Lucraa+
          </Link>
          <Link className="btn btn-primary btn-sm" to="/demo" data-cta="conteudo_nav_demo">
            Ver demonstração
          </Link>
        </div>
      </div>
    </header>
  );
}

export function ConteudoShell({
  breadcrumb,
  children,
}: {
  breadcrumb: ReadonlyArray<{ nome: string; path: string }>;
  children: ReactNode;
}) {
  return (
    <div className="lp-root">
      <div className="lp-hero-bg" style={{ minHeight: "100vh" }}>
        <ConteudoNav />

        <main className="lp-wrap legal-doc" aria-labelledby="conteudo-h1">
          <Breadcrumb itens={breadcrumb} />
          {children}
        </main>

        <SiteFooter />
      </div>
    </div>
  );
}
